import * as Sentry from '@sentry/react'
import { useRouter } from '@tanstack/react-router'
import { type ReactNode, useEffect } from 'react'

// Initialize Sentry (deferred to idle/visibility)
if (typeof window !== 'undefined') {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE

  const initSentry = () => {
    if (!dsn) return
    const start = () => {
      Sentry.init({
        dsn,
        environment,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        // Performance Monitoring (disable in dev)
        tracesSampleRate: environment === 'production' ? 0.1 : 0,
        // Session Replay
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        // Release tracking
        release: import.meta.env.VITE_APP_VERSION,
        // Don't send errors in development (optional)
        enabled: environment !== 'development',
      })
    }

    if (document.visibilityState === 'visible') {
      start()
    } else {
      const onVisible = () => {
        document.removeEventListener('visibilitychange', onVisible)
        start()
      }
      document.addEventListener('visibilitychange', onVisible, { once: true })
    }
  }

  // Defer to idle
  if (window.requestIdleCallback) {
    window.requestIdleCallback(initSentry)
  } else {
    setTimeout(initSentry, 0)
  }
}

export function SentryProvider({ children }: { children: ReactNode }) {
  const router = useRouter()

  useEffect(() => {
    if (!router) return

    // Track navigation as breadcrumbs
    const unsubscribe = router.subscribe('onLoad', ({ toLocation }) => {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: `Navigated to ${toLocation.pathname}`,
        level: 'info',
      })
    })

    return () => unsubscribe()
  }, [router])

  return <>{children}</>
}

// Error boundary wrapper
export const SentryErrorBoundary = Sentry.ErrorBoundary

// Convenience exports
export { Sentry }

// Helper to capture errors with context
export const captureError = (error: Error, context?: Record<string, unknown>) => {
  if (context) {
    Sentry.setContext('error_context', context)
    // If request ID is in context, also set it as a tag for easier filtering
    if (context.requestId && typeof context.requestId === 'string') {
      Sentry.setTag('request_id', context.requestId)
    }
  }
  Sentry.captureException(error)
}

// Helper to set user context
export const setUser = (user: { id: string; email?: string; name?: string }) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  })
}

// Helper to clear user context (on logout)
export const clearUser = () => {
  Sentry.setUser(null)
}
